import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { A } from '@expo/html-elements';
import {
  BodyText,
  checkIfRecaptchaEnabled,
  Form,
  TEST_GRECAPTCHA,
  TextLink,
  theme,
  useNoticeBannersStackContext,
  useReCaptcha,
} from '@sweetgreen/garnish';

import {
  useHasFailedToFetchCustomerData,
  useHasLoggedIn,
  useIsLoggedIn,
  useIsLoggingIn,
} from '@order/AuthMachine';
import { PageWrapper } from '@order/components';
import { useCustomer, useSignUpVerification } from '@order/Customer';
import { useLocalizationContext } from '@order/Localization';

import {
  useCloseActiveScreensOnAuthStatusChange,
  useNavigateBack,
  useRedirectAfterAuthStateChange,
} from '../../navigation';
import type { JoinVerificationScreenProps } from '../../navigation/AppNavigation.props';
import {
  isEmailValid,
  useJoinVerificationForm,
} from './JoinVerificationScreen.hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const JoinVerificationScreen = (props: JoinVerificationScreenProps) => {
  const { route, navigation } = props;

  const {
    email = '',
    token = '',
    redirect,
    redirectToPlanId,
    redirectToCampaignId,
  } = route.params ?? {};

  const isLoggedIn = useIsLoggedIn();
  const hasLoggedIn = useHasLoggedIn();
  const isLoggingIn = useIsLoggingIn();
  const { customer } = useCustomer();

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const redirectAfterAuthStateChange = useRedirectAfterAuthStateChange();
  const isValidEmailProvided = isEmailValid(email);
  const navigateBack = useNavigateBack();

  const customerFirstName = customer?.firstName;

  const {
    verifySignUp,
    resendVerificationCode,
    isResendingVerificationCode,
    hasVerifiedCustomerRegistration,
  } = useSignUpVerification();

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasFailedToFetchCustomerData = useHasFailedToFetchCustomerData();

  // NOTE: After successful registration verification, the user's profile is rarely not completely
  //       formed, resulting in an odd auth status. To prevent this, we redirect those users to
  //       the Login screen and provide the appropriate error message.
  const shouldRedirectAfterFailedCustomerDataQuery =
    !isLoggedIn &&
    hasVerifiedCustomerRegistration &&
    hasFailedToFetchCustomerData;

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    if (isLoggingIn) return;

    if (isLoggedIn) {
      redirectAfterAuthStateChange();

      return;
    }

    if (!isValidEmailProvided) navigateBack();
  }, [
    isLoggedIn,
    isLoggingIn,
    isValidEmailProvided,
    navigateBack,
    redirectAfterAuthStateChange,
  ]);

  // Navigate to "Login" screen if retrieving customer data fails after verification.
  useEffect(() => {
    if (!shouldRedirectAfterFailedCustomerDataQuery) return;

    addNoticeBanner({
      text: t('join-verification.failed-to-fetch-customer-error'),
      palette: 'neutral',
    });

    navigation.replace('Login', {
      redirect,
      redirectToPlanId,
      redirectToCampaignId,
    });
  }, [
    addNoticeBanner,
    navigation,
    redirect,
    redirectToPlanId,
    redirectToCampaignId,
    shouldRedirectAfterFailedCustomerDataQuery,
    t,
  ]);

  // Display a success banner if the customer's registration was successful.
  useEffect(() => {
    if (!hasLoggedIn) return;

    addNoticeBanner({
      text: t('join-verification.welcome', { firstName: customerFirstName }),
      palette: 'success',
    });
  }, [addNoticeBanner, customerFirstName, hasLoggedIn, t]);

  // ────────────────────────────────────────────────────────────────────────────────

  return (
    <JoinVerificationForm
      testID="join-verification-form"
      email={email}
      token={token}
      verify={verifySignUp}
      resendCode={resendVerificationCode}
      isValidEmailProvided={isValidEmailProvided}
      isLoggingIn={isLoggingIn}
      isResendCodeLinkEnabled={!isResendingVerificationCode}
    />
  );
};

export const JoinVerificationForm = (props: JoinVerificationFormProps) => {
  const {
    testID,
    email,
    token,
    verify,
    resendCode,
    isValidEmailProvided,
    isLoggingIn,
    isResendCodeLinkEnabled,
  } = props;

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const closeActiveScreensOnAuthStatusChange =
    useCloseActiveScreensOnAuthStatusChange();

  // ─── ReCaptcha ───────────────────────────────────────────────────

  const { reCaptchaMachine, reCaptchaRef } = useReCaptcha();

  const [reCaptchaState] = reCaptchaMachine;
  const isLoadingRecaptcha = reCaptchaState.matches('loading');
  const isExecutingRecaptcha = reCaptchaState.matches('executing');

  // ────────────────────────────────────────────────────────────────────

  const { form } = useJoinVerificationForm({
    defaultValues: { code: token },
  });
  const { control, formState, handleSubmit } = form;
  const { errors, isSubmitting } = formState;

  const shouldAutoVerify = isValidEmailProvided && token.length > 1;

  // ────────────────────────────────────────────────────────────────────

  const onSubmitValid = React.useCallback(
    async (values) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const verifySignUpResponse = await verify({ ...values, email });

      const isSuccessfulVerification =
        verifySignUpResponse?.__typename === 'VerifyRegistrationSuccess';

      // close other open screens and modals, to prevent unnecessary network calls and state updates
      if (isSuccessfulVerification) closeActiveScreensOnAuthStatusChange();
    },
    [closeActiveScreensOnAuthStatusChange, email, verify],
  );

  const submitForm = React.useMemo(
    () => handleSubmit(onSubmitValid),
    [handleSubmit, onSubmitValid],
  );

  const resendVerificationCode = useCallback(
    async (grecaptcha = TEST_GRECAPTCHA) => {
      if (!isValidEmailProvided) return;

      await resendCode({ email, grecaptcha });

      // reset recaptcha after request
      reCaptchaRef.current?.resetRecaptcha();
    },
    [email, isValidEmailProvided, reCaptchaRef, resendCode],
  );

  const onResendVerificationCode = useCallback(async () => {
    if (!isValidEmailProvided) return;

    const isRecaptchaEnabled = checkIfRecaptchaEnabled();

    await (isRecaptchaEnabled
      ? reCaptchaRef.current?.executeRecaptcha()
      : resendVerificationCode());
  }, [isValidEmailProvided, reCaptchaRef, resendVerificationCode]);

  const onRecaptchaError = useCallback(() => {
    addNoticeBanner({ text: t('general.error'), palette: 'caution' });
  }, [addNoticeBanner, t]);

  // ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (shouldAutoVerify) void submitForm();
  }, [shouldAutoVerify, submitForm]);

  // ─────────────────────────────────────────────────────────────────

  const shouldDisableVerifyBtn = isSubmitting || isLoggingIn;
  const shouldDisableResendCodeBtn =
    shouldDisableVerifyBtn ||
    !isResendCodeLinkEnabled ||
    isLoadingRecaptcha ||
    isExecutingRecaptcha;

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper>
      <Form.Container
        testID={testID}
        reCaptchaRef={reCaptchaRef}
        reCaptchaMachine={reCaptchaMachine}
        onRecaptchaVerify={resendVerificationCode}
        onRecaptchaError={onRecaptchaError}
      >
        <Form.Title>{t('join-verification.title')}</Form.Title>

        <Form.Subtitle>
          <BodyText size={3}>
            {t('join-verification.subtitle', { email })}
          </BodyText>
        </Form.Subtitle>

        <Form.NumberFieldCode
          name="code"
          testID="field-code"
          control={control}
          rules={{ required: true }}
          label={t('form.enter-code')}
          notice={errors.code?.message}
          maxLength={6}
          onSubmit={submitForm}
          hideSubmitButton
        />

        <Form.SubmitButton
          testID="button-verify"
          accessibilityLabel={t('join-verification.a11y')}
          isLoading={shouldDisableVerifyBtn}
          onPress={submitForm}
        >
          {t('general.continue')}
        </Form.SubmitButton>

        <View style={styles.linksWrapper}>
          <View style={styles.linksRow}>
            <BodyText size={4}>
              {t('join-verification.dont-see-an-email')}{' '}
            </BodyText>

            <TextLink
              testID="link-resend-code"
              onPress={onResendVerificationCode}
              disabled={shouldDisableResendCodeBtn}
              size={4}
            >
              {t('general.resend')}
            </TextLink>
          </View>

          <View style={styles.linksRow}>
            <BodyText size={4}>
              {t('join-verification.having-trouble')}{' '}
            </BodyText>

            <A href={`mailto:${SG_SUPPORT_EMAIL}`}>
              <TextLink size={4}>{SG_SUPPORT_EMAIL}</TextLink>
            </A>
          </View>
        </View>
      </Form.Container>
    </PageWrapper>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type JoinVerificationFormProps = Readonly<{
  email: string;
  token: string;
  verify: ReturnType<typeof useCustomer>['verifySignUp'];
  resendCode: ReturnType<typeof useCustomer>['resendVerificationCode'];
  isResendCodeLinkEnabled: boolean;
  isValidEmailProvided: boolean;
  isLoggingIn: boolean;
}> &
  Pick<ViewProps, 'testID'>;

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  linksWrapper: {
    paddingTop: theme.spacing['10'],
  },
  linksRow: {
    flexDirection: 'row',
    paddingBottom: theme.spacing['2'],
  },
});

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const SG_SUPPORT_EMAIL = 'hello@sweetgreen.com';
